import { useState, useEffect, useRef, useCallback } from 'react'
import clsx from 'clsx'
import { Search, X, Loader2, AlertCircle } from 'lucide-react'
import { skillsService } from '@/services/skillsService'
import type { SkillSearchResult, SkillCategory } from '@/types'

interface SkillAutocompleteProps {
  onSelect: (skill: SkillSearchResult) => void
  selectedSkills?: string[] // Array of selected skill IDs to prevent duplicates
  categoryFilter?: SkillCategory
  placeholder?: string
  label?: string
  error?: string
  className?: string
  disabled?: boolean
}

const SkillAutocomplete = ({
  onSelect,
  selectedSkills = [],
  categoryFilter,
  placeholder = 'Search for skills...',
  label,
  error,
  className,
  disabled = false,
}: SkillAutocompleteProps) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SkillSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [searchError, setSearchError] = useState<string>('')
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search function
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([])
        setIsOpen(false)
        setSearchError('')
        return
      }

      setIsLoading(true)
      setSearchError('')

      try {
        const response = await skillsService.searchSkills({
          q: searchQuery,
          category: categoryFilter,
          limit: 20,
        })

        setResults(response.results)
        setIsOpen(true)
        setSelectedIndex(-1)
      } catch (err: any) {
        console.error('Skill search error:', err)
        setSearchError(err.response?.data?.error || 'Failed to search skills')
        setResults([])
        setIsOpen(false)
      } finally {
        setIsLoading(false)
      }
    },
    [categoryFilter]
  )

  // Handle input change with debouncing
  const handleInputChange = (value: string) => {
    setQuery(value)

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer for 300ms debounce
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value)
    }, 300)
  }

  // Handle skill selection
  const handleSelect = (skill: SkillSearchResult) => {
    if (selectedSkills.includes(skill.id)) {
      return // Prevent duplicate selection
    }

    onSelect(skill)
    setQuery('')
    setResults([])
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Clear search
  const handleClear = () => {
    setQuery('')
    setResults([])
    setIsOpen(false)
    setSearchError('')
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  // Get category badge color
  const getCategoryColor = (category: SkillCategory) => {
    // Normalize category to lowercase and trim whitespace
    const normalizedCategory = category?.toLowerCase().trim() as SkillCategory

    const colors: Record<SkillCategory, string> = {
      language: 'bg-blue-100 text-blue-700',
      framework: 'bg-purple-100 text-purple-700',
      library: 'bg-green-100 text-green-700',
      database: 'bg-orange-100 text-orange-700',
      tool: 'bg-gray-100 text-gray-700',
      cloud: 'bg-sky-100 text-sky-700',
      other: 'bg-neutral-100 text-neutral-700',
    }
    return colors[normalizedCategory] || colors.other
  }

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-neutral-700 mb-1.5">{label}</label>
      )}

      <div className="relative">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={clsx(
              error || searchError ? 'input-error' : 'input',
              'pl-10',
              (query || isLoading) && 'pr-10',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            autoComplete="off"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          )}
          {query && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && results.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          >
            {results.map((skill, index) => {
              const isSelected = selectedSkills.includes(skill.id)
              const isHighlighted = index === selectedIndex

              return (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => !isSelected && handleSelect(skill)}
                  disabled={isSelected}
                  className={clsx(
                    'w-full px-4 py-2.5 text-left flex items-center justify-between gap-2 transition-colors',
                    isHighlighted && !isSelected && 'bg-primary-50',
                    isSelected && 'bg-neutral-50 cursor-not-allowed opacity-50',
                    !isSelected && !isHighlighted && 'hover:bg-neutral-50'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="font-medium text-neutral-900 truncate">{skill.name}</span>
                    {isSelected && <span className="text-xs text-neutral-500">(Added)</span>}
                  </div>
                  <span
                    className={clsx(
                      'px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap',
                      getCategoryColor(skill.category)
                    )}
                  >
                    {skill.category}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {/* Empty state */}
        {isOpen && !isLoading && results.length === 0 && query.length >= 2 && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg p-4 text-center text-neutral-500"
          >
            No skills found for "{query}"
          </div>
        )}

        {/* Minimum character warning */}
        {query.length > 0 && query.length < 2 && (
          <p className="mt-1.5 text-sm text-neutral-500">Type at least 2 characters to search</p>
        )}
      </div>

      {/* Error message */}
      {(error || searchError) && (
        <p className="mt-1.5 text-sm text-error-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error || searchError}
        </p>
      )}
    </div>
  )
}

export default SkillAutocomplete
